import React, { useState, useEffect, useRef, useMemo } from "react";
import Select from "react-select";
import urls from "../../constants";
import FetchData, { GetList } from "../../providers/dataProvider";

const BOOTSTRAP_FIELD_HEIGHT = 35;
const BOOTSTRAP_FIELD_BORDER_COLOR = "#B4BABF";
const BOOTSTRAP_FIELD_FOCUS_COLOR = "#86b7fe";
const BOOTSTRAP_FIELD_BORDER_RADIUS = 6;
const BOOTSTRAP_FIELD_FONT_SIZE = 14;

const buildBootstrapSelectStyles = (overrides = {}) => ({
    control: (base, state) => ({
        ...base,
        boxShadow: "none",
        border: `1px solid ${state.isFocused ? BOOTSTRAP_FIELD_FOCUS_COLOR : BOOTSTRAP_FIELD_BORDER_COLOR}`,
        backgroundColor: "#F9FBFC",
        fontSize: `${BOOTSTRAP_FIELD_FONT_SIZE}px`,
        minHeight: `${BOOTSTRAP_FIELD_HEIGHT}px`,
        height: `${BOOTSTRAP_FIELD_HEIGHT}px`,
        borderRadius: `${BOOTSTRAP_FIELD_BORDER_RADIUS}px`,
        color: "#000811",
        whiteSpace: "nowrap",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        ":hover": {
            borderColor: state.isFocused ? BOOTSTRAP_FIELD_FOCUS_COLOR : BOOTSTRAP_FIELD_BORDER_COLOR,
        },
        ...overrides.control,
    }),
    valueContainer: (base) => ({
        ...base,
        alignItems: "center",
        display: "flex",
        minHeight: `${BOOTSTRAP_FIELD_HEIGHT - 2}px`,
        height: `${BOOTSTRAP_FIELD_HEIGHT - 2}px`,
        padding: "0 10px",
        overflow: "hidden",
        ...overrides.valueContainer,
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        lineHeight: "1.2",
        fontSize: `${BOOTSTRAP_FIELD_FONT_SIZE}px`,
        ...overrides.input,
    }),
    singleValue: (base) => ({
        ...base,
        alignItems: "center",
        display: "flex",
        height: "100%",
        margin: 0,
        maxWidth: "100%",
        position: "static",
        top: "auto",
        transform: "none",
        lineHeight: "1.2",
        color: "#000811",
        fontWeight: 500,
        ...overrides.singleValue,
    }),
    placeholder: (base) => ({
        ...base,
        alignItems: "center",
        display: "flex",
        height: "100%",
        margin: 0,
        position: "static",
        top: "auto",
        transform: "none",
        lineHeight: "1.2",
        color: "#6c757d",
        ...overrides.placeholder,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        alignItems: "center",
        height: `${BOOTSTRAP_FIELD_HEIGHT - 2}px`,
        ...overrides.indicatorsContainer,
    }),
    clearIndicator: (base) => ({
        ...base,
        alignItems: "center",
        display: "flex",
        height: `${BOOTSTRAP_FIELD_HEIGHT - 2}px`,
        justifyContent: "center",
        padding: 0,
        width: "32px",
        ...overrides.clearIndicator,
    }),
    dropdownIndicator: (base) => ({
        ...base,
        alignItems: "center",
        display: "flex",
        height: `${BOOTSTRAP_FIELD_HEIGHT - 2}px`,
        justifyContent: "center",
        padding: 0,
        width: "32px",
        color: "#000811",
        ...overrides.dropdownIndicator,
    }),
    indicatorSeparator: (base) => ({
        ...base,
        alignSelf: "center",
        height: "20px",
        marginBottom: 0,
        marginTop: 0,
        ...overrides.indicatorSeparator,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 100000,
        ...overrides.menuPortal,
    }),
    menu: (base) => ({
        ...base,
        zIndex: 100000,
        ...overrides.menu,
    }),
});
const QUOTE_STATUS_SELECT_HEIGHT = 24;
const QUOTE_STATUS_SELECT_BORDER_COLOR = "#c9d8e6";
const QUOTE_STATUS_SELECT_FOCUS_COLOR = "#8ab9f6";
const QUOTE_STATUS_SELECT_BORDER_RADIUS = 6;
const QUOTE_STATUS_SELECT_FONT_SIZE = 10.5;
const QUOTE_STATUS_SELECT_TEXT_COLOR = "#07192d";
const sizeSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: `${QUOTE_STATUS_SELECT_HEIGHT}px`,
        height: `${QUOTE_STATUS_SELECT_HEIGHT}px`,
        borderColor: state.isFocused ? QUOTE_STATUS_SELECT_FOCUS_COLOR : QUOTE_STATUS_SELECT_BORDER_COLOR,
        borderStyle: "solid",
        borderWidth: 1,
        borderRadius: `${QUOTE_STATUS_SELECT_BORDER_RADIUS}px`,
        backgroundColor: "#f8fbff",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(0, 87, 149, 0.08)" : "none",
        fontSize: `${QUOTE_STATUS_SELECT_FONT_SIZE}px`,
        fontWeight: 400,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: state.isDisabled ? 0.65 : 1,
        ":hover": {
            borderColor: state.isFocused ? QUOTE_STATUS_SELECT_FOCUS_COLOR : QUOTE_STATUS_SELECT_BORDER_COLOR,
        },
    }),
    valueContainer: (base) => ({
        ...base,
        minHeight: `${QUOTE_STATUS_SELECT_HEIGHT - 2}px`,
        height: `${QUOTE_STATUS_SELECT_HEIGHT - 2}px`,
        padding: "1px 7px",
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        overflow: "hidden",
    }),
    indicatorsContainer: (base) => ({
        ...base,
        minHeight: `${QUOTE_STATUS_SELECT_HEIGHT - 2}px`,
        height: `${QUOTE_STATUS_SELECT_HEIGHT - 2}px`,
        display: "flex",
        alignItems: "center",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        height: "22px",
        width: "22px",
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: QUOTE_STATUS_SELECT_TEXT_COLOR,
    }),
    clearIndicator: (base) => ({
        ...base,
        height: "100%",
        padding: "0 10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    }),
    indicatorSeparator: (base) => ({
        ...base,
        display: "none",
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: `${QUOTE_STATUS_SELECT_FONT_SIZE}px`,
        fontWeight: 400,
        color: QUOTE_STATUS_SELECT_TEXT_COLOR,
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        maxWidth: "100%",
        position: "static",
        transform: "none",
        display: "flex",
        alignItems: "center",
        color: QUOTE_STATUS_SELECT_TEXT_COLOR,
        fontSize: `${QUOTE_STATUS_SELECT_FONT_SIZE}px`,
        fontWeight: 400,
        lineHeight: "1.5",
    }),
    placeholder: (base) => ({
        ...base,
        margin: 0,
        position: "static",
        transform: "none",
        display: "flex",
        alignItems: "center",
        color: "#40586f",
        fontSize: `${QUOTE_STATUS_SELECT_FONT_SIZE}px`,
        fontWeight: 400,
        lineHeight: "1.5",
    }),
};
const sizeSelectPortalTarget =
    typeof document !== "undefined" ? document.body : null;

const inlineSelectTriggerStyle = {
    width: "100%",
    minHeight: `${BOOTSTRAP_FIELD_HEIGHT - 9}px`,
    padding: "2px 10px",
    border: "1px solid #c9d8e6",
    borderRadius: `${BOOTSTRAP_FIELD_BORDER_RADIUS}px`,
    backgroundColor: "#f8fbff",
    color: "#000811",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    fontSize: "12px",
    fontWeight: 400,
    cursor: "pointer",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
};

export const InlineSelectToggleCell = ({
    value,
    placeholder = "Select",
    options = [],
    loading = false,
    disabled = false,
    onChange,
    onBlur,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const justSelectedRef = useRef(false);

    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    useEffect(() => {
        onChangeRef.current = onChange;
        onBlurRef.current = onBlur;
    }, [onChange, onBlur]);

    const selectedOption = value ? { label: value, value } : null;
    const triggerLabel = loading ? "Loading..." : value || placeholder;

    const closeEditor = () => setIsEditing(false);
    const openEditor = () => {
        if (!disabled && !loading) {
            justSelectedRef.current = false;
            setIsEditing(true);
        }
    };

    if (!isEditing) {
        return (
            <button
                type="button"
                className="size-inline-select-trigger"
                style={inlineSelectTriggerStyle}
                onClick={openEditor}
                title={triggerLabel}
                disabled={disabled || loading}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{triggerLabel}</span>
                <i className="fas fa-chevron-down" style={{ flex: "0 0 auto", color: "#394049" }}></i>
            </button>
        );
    }

    return (
        <Select
            autoFocus
            menuIsOpen
            options={options}
            value={selectedOption}
            isSearchable={true}
            isClearable={true}
            backspaceRemovesValue={true}
            escapeClearsValue={true}
            isDisabled={disabled || loading}
            onChange={(option) => {
                justSelectedRef.current = true;
                onChangeRef.current?.(option);
                closeEditor();
                setTimeout(() => {
                    onBlurRef.current?.();
                }, 0);
            }}
            onBlur={() => {
                if (justSelectedRef.current) {
                    closeEditor();
                    return;
                }
                onBlurRef.current?.();
                closeEditor();
            }}
            onMenuClose={closeEditor}
            placeholder={placeholder}
            classNamePrefix="size-select"
            components={{ ClearIndicator: () => null }}
            menuPortalTarget={sizeSelectPortalTarget}
            menuPosition={sizeSelectPortalTarget ? "fixed" : "absolute"}
            styles={sizeSelectStyles}
        />
    );
};

const variantOptionsCache = new Map();
const variantCombinationsCache = new Map();

const normalizeVariantName = (name) => (name || "").toString().trim().toLowerCase();

const getConfigurationValue = (configuration, attributeId) =>
    configuration?.find((item) => item?.attributeId === attributeId)?.valueName || "";

const getRowVariantValue = (rowVariants, matcher) =>
    rowVariants?.find((item) => matcher(normalizeVariantName(item?.variantName)))?.variantValue || "";

const rowFilterAnchor = new Map();

const getCombinationFilteredOptions = (varitem, rowVariants, combinations, allOptions, anchorVariantId) => {
    const safeOptions = Array.isArray(allOptions) ? allOptions : [];
    const combinationVariantIds = new Set();
    combinations.forEach((combo) => {
        combo?.options?.forEach((opt) => combinationVariantIds.add(opt.variantId));
    });

    if (!combinationVariantIds.has(varitem?.variantId)) {
        return { options: safeOptions, waiting: false };
    }

    if (!anchorVariantId || anchorVariantId === varitem?.variantId) {
        return { options: safeOptions, waiting: false };
    }

    const anchorSibling = rowVariants.find((v) => v?.variantId === anchorVariantId);

    if (!anchorSibling?.variantValue) {
        return { options: safeOptions, waiting: false };
    }

    const matchingValues = new Set();
    combinations.forEach((combo) => {
        const anchorMatch = combo?.options?.some(
            (opt) => opt?.variantId === anchorSibling.variantId && opt?.value === anchorSibling.variantValue
        );
        if (anchorMatch) {
            const matchOpt = combo?.options?.find((opt) => opt?.variantId === varitem?.variantId);
            if (matchOpt?.value) matchingValues.add(matchOpt.value);
        }
    });

    return {
        options: safeOptions.filter((opt) => matchingValues.has(opt)),
        waiting: false,
    };
};
const buildTransitionTypeParams = (configuration, rowVariants, productConfig) => {
    const getValue = (attributeId) =>
        configuration.find(x => x.attributeId === attributeId)?.valueName || "None";

    return {
        DuctWidth: getRowVariantValue(rowVariants, name => name.includes("width")),
        DuctHeight: getRowVariantValue(rowVariants, name => name.includes("height")),

        Model: productConfig?.productName?.productName,
        MountingOrientation: getValue(urls.CFSD_MOUNTING_ORIENTATION),
        TemperatureRating: getValue(urls.CFSD_TEMPERATURE_CLASS),
        Material: getValue(urls.CEILING_MATERIAL),
        VelocityRating: getValue(urls.VELOCITY_RATING),
        PressureRating: getValue(urls.PRESSURE_RATING),
        Sizing: getValue(urls.SIZING),
    };
};

const TransitionTypeSelectCell = ({
    configuration = [],
    rowVariants = [],
    varitem,
    productindex,
    variantValue,
    HandleProductOnchange,
    HandleProductonfocusout,
    productConfig
}) => {
    const transitionParams = useMemo(() => {

        return buildTransitionTypeParams(configuration, rowVariants, productConfig);
    }, [configuration, rowVariants, productConfig]);
    const { data: transitionTypeData, loading: transitionTypeLoading } = FetchData("transitiontype", transitionParams);
    const rawTransitionOptions = Array.isArray(transitionTypeData)
        ? transitionTypeData
        : Array.isArray(transitionTypeData?.data)
            ? transitionTypeData.data
            : [];
    const selectOptions = rawTransitionOptions
        .map((item) => {
            const value =
                item?.transitionType ??
                item?.transitiontype ??
                item?.name ??
                item?.value ??
                item?.label;

            return value ? { label: value, value } : null;
        })
        .filter(Boolean);

    return (
        <InlineSelectToggleCell
            value={variantValue}
            placeholder={transitionTypeLoading ? "Loading..." : "Select"}
            options={selectOptions}
            loading={transitionTypeLoading}
            disabled={selectOptions.length === 0}
            onChange={(option) => HandleProductOnchange(option, varitem)}
            onBlur={() => HandleProductonfocusout(varitem, productindex)}
        />
    );
};


const buildActuatorLocationParams = (configuration, rowVariants, productConfig) => {
    const getValue = (attributeId) =>
        configuration.find(x => x.attributeId === attributeId)?.valueName || "None";

    return {
        Model: productConfig?.productName?.productName,
        MountingOrientation: getValue(urls.CFSD_MOUNTING_ORIENTATION),
    };
};

const ActuatorLocationSelectCell = ({
    configuration = [],
    rowVariants = [],
    varitem,
    productindex,
    variantValue,
    HandleProductOnchange,
    HandleProductonfocusout,
    productConfig
}) => {
    const actuatorParams = useMemo(
        () => buildActuatorLocationParams(configuration, rowVariants, productConfig),
        [configuration, rowVariants, productConfig]
    );
    const { data: actuatorData, loading: actuatorLoading } = FetchData("actuatorlocation", actuatorParams);

    const rawActuatorOptions = Array.isArray(actuatorData)
        ? actuatorData
        : Array.isArray(actuatorData?.data)
            ? actuatorData.data
            : [];

    const selectOptions = rawActuatorOptions
        .map((item) => {
            const value =
                item?.actuatorLocation ??
                item?.actuatorlocation ??
                item?.name ??
                item?.value ??
                item?.label;
            return value ? { label: value, value } : null;
        })
        .filter(Boolean);

    return (
        <InlineSelectToggleCell
            value={variantValue}
            placeholder={actuatorLoading ? "Loading..." : "Select"}
            options={selectOptions}
            loading={actuatorLoading}
            disabled={selectOptions.length === 0}
            onChange={(option) => HandleProductOnchange(option, varitem)}
            onBlur={() => HandleProductonfocusout(varitem, productindex)}
        />
    );
};

export const useVariantOptions = (productId) => {
    const [optionsMap, setOptionsMap] = useState(
        productId ? variantOptionsCache.get(productId) ?? null : null
    );
    const [combinations, setCombinations] = useState(
        productId ? variantCombinationsCache.get(productId) ?? [] : []
    );
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!productId) {
            setOptionsMap(null);
            setCombinations([]);
            return;
        }

        if (variantOptionsCache.has(productId)) {
            setOptionsMap(variantOptionsCache.get(productId));
            setCombinations(variantCombinationsCache.get(productId) ?? []);
            return;
        }

        setLoading(true);

        GetList("productvariantoptions", { id: productId }, (response) => {
            setLoading(false);

            const raw = response?.data ?? response;

            const variantsList = Array.isArray(raw?.variants)
                ? raw.variants
                : Array.isArray(raw)
                    ? raw
                    : [];
            const combinationsList = Array.isArray(raw?.combinations) ? raw.combinations : [];

            const map = {};
            variantsList.forEach((item) => {
                if (item?.variantId === undefined || item?.variantId === null) return;

                map[item.variantId] = {
                    variantName: item.variantName,
                    inputMode: item.inputMode,
                    options: Array.isArray(item.options) ? item.options : [],
                };
            });

            variantOptionsCache.set(productId, map);
            variantCombinationsCache.set(productId, combinationsList);
            setOptionsMap(map);
            setCombinations(combinationsList);
        });
    }, [productId]);

    return { optionsMap, combinations, loading };
};

export const SizeFieldCell = ({
    optionsMap,
    optionsLoading,
    actuatorLocationOptions = [],
    actuatorLocationLoading = false,
    configuration = [],
    varitem,
    productindex,
    invalidUnitPrice,
    HandleProductOnchange,
    HandleProductonfocusout,
    handleKeyDown,
    rowVariants = [],
    combinations = [],
    productConfig
}) => {
    const variantValue = varitem.variantValue || "";
    const meta = optionsMap?.[varitem?.variantId];
    const isVavModelField = urls?.VAV_MODEL?.includes(varitem?.variantId);
    const isActuatorLocationField = urls?.ACTUATOR_LOCATION?.includes(varitem?.variantId);
    const isTransitionTypeField = urls?.CFSD_TRANSITION_TYPE?.includes(varitem?.variantId);
    const useDropdown = meta?.inputMode === "select" && (meta?.options?.length ?? 0) > 0;
    const variantName = (varitem?.variantName || "").toLowerCase();
    const isHeightField = variantName.includes("height");
    const isOrientationField = variantName.trim() === "orientation" || variantName.includes("orientation");
    const isInletDiaField = variantName.includes("dia");
    const isVavModelNameField = variantName.includes("vav");
    const widthSibling = rowVariants?.find((v) => (v?.variantName || "").toLowerCase().includes("width"));
    const vavModelSibling = rowVariants?.find((v) => (v?.variantName || "").toLowerCase().includes("vav"));
    const inletDiaSibling = rowVariants?.find((v) => (v?.variantName || "").toLowerCase().includes("dia"));

    const vavColumnIndex = vavModelSibling ? rowVariants.indexOf(vavModelSibling) : -1;
    const inletColumnIndex = inletDiaSibling ? rowVariants.indexOf(inletDiaSibling) : -1;
    const inletIsPrimary = inletColumnIndex !== -1 && (vavColumnIndex === -1 || inletColumnIndex < vavColumnIndex);

    const isNumericField =
        variantName.includes("quantity") ||
        variantName.includes("qty") ||
        variantName.includes("diameter") ||
        variantName.includes("width") ||
        variantName.includes("height") ||
        variantName.includes("length") ||
        variantName.includes("price");


    const combinationVariantIds = useMemo(() => {
        const ids = new Set();
        combinations.forEach((combo) => combo?.options?.forEach((opt) => ids.add(opt.variantId)));
        return ids;
    }, [combinations]);

    const participatesInCombinations = combinationVariantIds.has(varitem?.variantId);
    const explicitAnchor = rowFilterAnchor.get(productindex);

    const vavModelVariant = rowVariants?.find((v) => urls?.VAV_MODEL?.includes(v?.variantId));

    const fallbackAnchorVariant = rowVariants?.find(
        (v) =>
            v?.variantId !== varitem?.variantId &&
            combinationVariantIds.has(v?.variantId) &&
            !!v?.variantValue
    );

    const primaryVariant = inletIsPrimary ? inletDiaSibling : vavModelVariant;
    const isPrimaryField = !!primaryVariant && varitem?.variantId === primaryVariant?.variantId;

    const anchorVariantId =
        explicitAnchor ||
        (primaryVariant?.variantValue ? primaryVariant.variantId : undefined) ||
        fallbackAnchorVariant?.variantId;

    const needsAnchorFirst =
        participatesInCombinations &&
        !!primaryVariant &&
        !isPrimaryField &&
        !primaryVariant?.variantValue;

    const { options: dropdownOptions } = useMemo(
        () => getCombinationFilteredOptions(varitem, rowVariants, combinations, meta?.options, anchorVariantId),
        [varitem, rowVariants, combinations, meta, anchorVariantId]
    );

    const isVavSecondary = isVavModelNameField && inletIsPrimary;
    const isInletSecondary = isInletDiaField && !inletIsPrimary;


    useEffect(() => {
        if (!(isVavSecondary || isInletSecondary)) return;
        if (!primaryVariant?.variantValue) return;
        if (variantValue) return;

        const matches = new Set();
        combinations.forEach((combo) => {
            const anchorMatch = combo?.options?.some(
                (opt) => opt?.variantId === primaryVariant.variantId && opt?.value === primaryVariant.variantValue
            );
            if (anchorMatch) {
                const matchOpt = combo?.options?.find((opt) => opt?.variantId === varitem?.variantId);
                if (matchOpt?.value) matches.add(matchOpt.value);
            }
        });

        if (matches.size === 1) {
            const onlyValue = [...matches][0];
            HandleProductOnchange({ label: onlyValue, value: onlyValue }, varitem);
        }
    }, [isVavSecondary, isInletSecondary, primaryVariant?.variantValue, primaryVariant?.variantId, variantValue, combinations, varitem, HandleProductOnchange]);

    useEffect(() => {
        if (!isOrientationField) return;

        if (primaryVariant?.variantValue && !variantValue) {
            HandleProductOnchange({ label: "Right", value: "Right" }, varitem);
        } else if (!primaryVariant?.variantValue && variantValue) {
            HandleProductOnchange({ label: "", value: "" }, varitem);
        }
    }, [isOrientationField, primaryVariant?.variantValue, variantValue, varitem, HandleProductOnchange]);


    useEffect(() => {
        if (participatesInCombinations && dropdownOptions.length === 1 && !needsAnchorFirst) {
            const singleValue = dropdownOptions[0];
            if (variantValue !== singleValue) {
                HandleProductOnchange({ label: singleValue, value: singleValue }, varitem);
            }
        }
    }, [participatesInCombinations, dropdownOptions, needsAnchorFirst, variantValue, varitem, HandleProductOnchange]);

    if (optionsLoading) {
        return (
            <input
                className="form-control"
                type="text"
                placeholder="Loading..."
                disabled
                value=""
                readOnly
            />
        );
    }

    if (isHeightField && combinationVariantIds.has(varitem?.variantId) && widthSibling && !widthSibling.variantValue) {
        return <></>;
    }
    if (
        isInletDiaField &&
        !inletIsPrimary &&
        vavModelSibling &&
        !vavModelSibling.variantValue &&
        combinationVariantIds.size > 0
    ) {
        return <></>;
    }

    if (isVavModelNameField && !isPrimaryField) {
        return <span className="size-readonly-text">{variantValue || ""}</span>;
    }

    if (isTransitionTypeField) {
        return (
            <TransitionTypeSelectCell
                configuration={configuration}
                rowVariants={rowVariants}
                varitem={varitem}
                productindex={productindex}
                variantValue={variantValue}
                HandleProductOnchange={HandleProductOnchange}
                HandleProductonfocusout={HandleProductonfocusout}
                productConfig={productConfig}
            />
        );
    }
    if (isActuatorLocationField) {
        return (
            <ActuatorLocationSelectCell
                configuration={configuration}
                rowVariants={rowVariants}
                varitem={varitem}
                productindex={productindex}
                variantValue={variantValue}
                HandleProductOnchange={HandleProductOnchange}
                HandleProductonfocusout={HandleProductonfocusout}
                productConfig={productConfig}
            />
        );
    }

    if (isOrientationField) {
        const selectOptions = ["Left", "Right"].map((opt) => ({ label: opt, value: opt }));

        return (
            <InlineSelectToggleCell
                value={variantValue}
                placeholder="Select"
                options={selectOptions}
                loading={false}
                disabled={!varitem?.inputByUser}
                onChange={(option) => HandleProductOnchange(option, varitem)}
                onBlur={() => HandleProductonfocusout(varitem, productindex)}
            />
        );
    }

    if (!useDropdown || (isHeightField && (dropdownOptions?.length ?? 0) === 0)) {
        return (
            <input
                className="form-control"
                type="text"
                inputMode={isNumericField ? "decimal" : "text"}
                autoComplete="off"
                placeholder="Enter"
                id={`id-${varitem?.variantId}-${varitem?.sid}`}
                name={`name-${varitem?.variantId}-${varitem?.sid}`}
                value={variantValue}
                onChange={(e) => HandleProductOnchange(e, varitem)}
                disabled={!varitem?.inputByUser}
                onKeyDown={(e) => {
                    handleKeyDown?.(e);
                    if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                    }
                }}
                onBlur={() => HandleProductonfocusout(varitem, productindex)}
                style={{
                    border: invalidUnitPrice?.find((invalidItem) => invalidItem?.sid === varitem?.sid)
                        ? "1px solid red"
                        : "",
                    textAlign:
                        typeof Number(variantValue) === "number" && !isNaN(Number(variantValue))
                            ? "right"
                            : "left",
                }}
            />
        );
    }

    const selectOptions = dropdownOptions.map((opt) => ({ label: opt, value: opt }));
    const hasError = invalidUnitPrice?.some((i) => i?.sid === varitem?.sid);
    const fieldDisabled = !varitem?.inputByUser;

    if (needsAnchorFirst) {
        return (
            <InlineSelectToggleCell
                value=""
                placeholder="Select"
                options={[]}
                loading={false}
                disabled={true}
                onChange={() => { }}
                onBlur={() => { }}
            />
        );
    }

    // Scenario 1: exactly one possible value -> plain text, no input box (like Unit Price column)
    if (dropdownOptions.length === 1) {
        return <>{dropdownOptions[0]}</>;
    }

    // Scenario 2 & 3: dropdown, filtered or full options
    return (
        <InlineSelectToggleCell
            value={variantValue}
            placeholder="Select"
            options={selectOptions}
            loading={false}
            disabled={fieldDisabled}
            onChange={(option) => {
                rowFilterAnchor.set(productindex, varitem?.variantId);
                HandleProductOnchange(option, varitem);
            }}
            onBlur={() => HandleProductonfocusout(varitem, productindex)}
        />
    );
};


const SQUARE_ATTRIBUTE_VALUE = "Square";

export const isSquareDiffuser = (configuration) => {
    if (!urls?.NECK_APPEARANCE) return false;

    return !!configuration?.some(
        (item) =>
            item?.attributeId === urls.NECK_APPEARANCE &&
            item?.valueName?.trim()?.toLowerCase() === SQUARE_ATTRIBUTE_VALUE.toLowerCase()
    );
};